/**
 * Accept / reject / cancel action handlers + their local state for
 * {@link ChangeRequestsTab}. Extracted into a hook so the tab component
 * stays under the 500-line file limit; behaviour is unchanged.
 *
 * @param line - the PO line whose proposed changes are being actioned
 * @param activeChangeFields - the fields with an open proposal (date/price/quantity)
 * @param dateLabel - the label of the date under request, as the card shows it
 *   (CR-7488: a customer's request is the Customer Required Date, not the promise date)
 * @param onUpdate - refetch callback fired after a successful action
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { PurchaseOrderLine, purchasingAPI } from '../../lib/api';
import type { ChangeField } from './changeRequestsTabHelpers';

export function useChangeRequestActions(
  line: PurchaseOrderLine,
  activeChangeFields: ChangeField[],
  dateLabel: string,
  onUpdate?: () => void,
) {
  // CR-7488: the toast names the field the user acted on, with the same date
  // label as the row they clicked — not "Promise date" for every date.
  const fieldLabel = (field: ChangeField) =>
    field === 'date' ? dateLabel : field === 'price' ? 'Unit price' : 'Quantity';

  // CR-8596: the server says WHY it refused ("This change was proposed by the
  // supplier; only the customer can reject it", "You do not have purchasing
  // access for this company"); a bare "1 change failed to reject" hid it from
  // the tester and from support.
  const refusalMessage = (err: unknown, field: ChangeField): string => {
    const axiosErr = err as { response?: { data?: { message?: string; detail?: string } } };
    const detail = axiosErr.response?.data?.message ?? axiosErr.response?.data?.detail;
    return detail ? `${fieldLabel(field)}: ${detail}` : `${fieldLabel(field)}: request failed`;
  };
  const failureToast = (verb: 'reject' | 'accept', messages: string[]) =>
    toast.error(`${messages.length} change${messages.length > 1 ? 's' : ''} failed to ${verb}: ${messages.join('; ')}`);

  const [fieldActionLoading, setFieldActionLoading] = useState<string | null>(null);
  const [rejectField, setRejectField] = useState<ChangeField | 'all' | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  // EXPEDITING-PARITY PR 2 (G8): the optional counter that turns a rejection
  // into this side's own proposal. Empty string = a plain rejection, which is
  // still the default — the counter is offered, never required.
  const [rejectCounter, setRejectCounter] = useState('');

  const handleAcceptField = async (field: ChangeField) => {
    setFieldActionLoading(field);
    try {
      const lineId = line.id;
      if (field === 'date') {
        await purchasingAPI.acceptDeliveryDate(lineId, {});
      } else if (field === 'quantity') {
        await purchasingAPI.acceptQuantityChange(lineId, {});
      } else if (field === 'price') {
        await purchasingAPI.acceptPriceChange(lineId, {});
      }
      toast.success(`${fieldLabel(field)} change accepted`);
      onUpdate?.();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      toast.error(axiosErr.response?.data?.message || 'Failed to accept change');
    } finally {
      setFieldActionLoading(null);
    }
  };

  const handleRejectField = async () => {
    if (!rejectField || !rejectReason.trim()) return;
    setFieldActionLoading(rejectField);
    const lineId = line.id;
    const targets: ChangeField[] = rejectField === 'all' ? activeChangeFields : [rejectField];
    try {
      // CR-4706 round 2: sequential, same rationale as handleAcceptAll —
      // parallel rejects race on the same PO line row and lose clears.
      const failures: string[] = [];
      const counter = rejectCounter.trim();
      for (const f of targets) {
        try {
          // A counter belongs to ONE field, so it rides only a single-field
          // rejection — "reject all with one counter" would have to guess which
          // of three proposals the value answers.
          await purchasingAPI.rejectFieldChange(lineId, {
            field: f, reason: rejectReason,
            ...(counter && targets.length === 1 ? { counter } : {}),
          });
        } catch (err: unknown) {
          failures.push(refusalMessage(err, f));
        }
      }
      if (failures.length === 0) {
        const what = fieldLabel(targets[0]);
        toast.success(targets.length > 1 ? 'All changes rejected'
          : counter ? `${what} change rejected — your counter-proposal is with them`
          : `${what} change rejected`);
      }
      else failureToast('reject', failures);
      setRejectField(null);
      setRejectReason('');
      setRejectCounter('');
      onUpdate?.();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      toast.error(axiosErr.response?.data?.message || 'Failed to reject change');
    } finally {
      setFieldActionLoading(null);
    }
  };

  const handleAcceptAll = async () => {
    setFieldActionLoading('all');
    const lineId = line.id;
    try {
      // CR-4706 round 2: process sequentially, NOT in parallel. Each accept
      // endpoint loads the PO line, clears one proposed_* field, and saves.
      // When these ran via Promise.allSettled the three calls raced on the
      // same row — each worker read a stale snapshot with all three
      // proposed_* fields still populated, cleared its own, and the last
      // writer clobbered the earlier clears. The UI then refreshed and
      // showed Quantity + Unit Price still "Active" even though all three
      // audit rows had been written. Sequential awaits avoid the race and
      // keep the per-field action separable (so a failure on one doesn't
      // abort the rest — we surface a partial-failure toast).
      const failures: string[] = [];
      for (const f of activeChangeFields) {
        try {
          if (f === 'date') await purchasingAPI.acceptDeliveryDate(lineId, {});
          else if (f === 'price') await purchasingAPI.acceptPriceChange(lineId, {});
          else await purchasingAPI.acceptQuantityChange(lineId, {});
        } catch (err: unknown) {
          failures.push(refusalMessage(err, f));
        }
      }
      if (failures.length === 0) toast.success('All changes accepted');
      else failureToast('accept', failures);
      onUpdate?.();
    } finally {
      setFieldActionLoading(null);
    }
  };

  // CR-4680: Supplier cancels their own proposed change
  const handleCancelField = async (field: ChangeField) => {
    setFieldActionLoading(field);
    try {
      const lineId = line.id;
      await purchasingAPI.cancelFieldChange(lineId, { field });
      toast.success(`${fieldLabel(field)} change cancelled`);
      onUpdate?.();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      toast.error(axiosErr.response?.data?.message || 'Failed to cancel change');
    } finally {
      setFieldActionLoading(null);
    }
  };

  return {
    fieldActionLoading,
    rejectField,
    setRejectField,
    rejectReason,
    setRejectReason,
    rejectCounter,
    setRejectCounter,
    handleAcceptField,
    handleRejectField,
    handleAcceptAll,
    handleCancelField,
  };
}
